import supabase from '../supabaseClient';
import { logOnboardingError } from './errorLogger';

export async function signInWithProvider(provider) {
  return supabase.auth.signInWithOAuth({
    provider,
    options: {
      redirectTo: `${window.location.origin}/onboarding`,
    },
  });
}

export async function signUpWithEmail({ fullName, email, password }) {
  const { data, error } = await supabase.auth.signUp({
    email,
    password,
    options: {
      data: {
        full_name: fullName,
      },
      emailRedirectTo: `${window.location.origin}/auth/callback`,
    },
  });

  // Log error if signup failed
  if (error) {
    await logOnboardingError({
      error,
      action: 'signup',
      userEmail: email,
      metadata: { fullName },
    });
  }

  return { data, error };
}

export async function signInWithEmail({ email, password }) {
  const { data, error } = await supabase.auth.signInWithPassword({
    email,
    password,
  });

  // Log error if signin failed
  if (error) {
    await logOnboardingError({
      error,
      action: 'signin',
      userEmail: email,
    });
  }

  return { data, error };
}

export async function getCurrentUser() {
  const { data, error } = await supabase.auth.getUser();
  return { data, error };
}

export async function fetchProfileByAuthId(authUserId, retries = 3) {
  // Add a small delay to ensure Supabase is fully initialized
  await new Promise(resolve => setTimeout(resolve, 100));

  for (let attempt = 0; attempt < retries; attempt++) {
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('auth_user_id', authUserId)
        .maybeSingle();

      // If successful or it's a "not found" error, return immediately
      if (!error || error.code === 'PGRST116') {
        return { data, error };
      }

      // If it's a network error and we have retries left, wait and retry
      if (error.message?.includes('Failed to fetch') && attempt < retries - 1) {
        console.warn(`fetchProfileByAuthId attempt ${attempt + 1} failed, retrying...`);
        await new Promise(resolve => setTimeout(resolve, 500 * (attempt + 1)));
        continue;
      }

      // For other errors, log and return
      console.error('fetchProfileByAuthId error:', error);
      await logOnboardingError({
        error,
        action: 'fetch_profile',
        authUserId,
        metadata: { attempt: attempt + 1, retries },
      });
      return { data: null, error };
    } catch (err) {
      console.error('fetchProfileByAuthId caught error:', err);
      if (attempt < retries - 1) {
        await new Promise(resolve => setTimeout(resolve, 500 * (attempt + 1)));
        continue;
      }
      await logOnboardingError({
        error: err,
        action: 'fetch_profile',
        authUserId,
        metadata: { attempt: attempt + 1, retries, reason: 'caught_exception' },
      });
      return { data: null, error: err };
    }
  }

  const finalError = new Error('Failed to fetch profile after retries');
  await logOnboardingError({
    error: finalError,
    action: 'fetch_profile',
    authUserId,
    metadata: { retries, reason: 'all_retries_exhausted' },
  });
  return { data: null, error: finalError };
}

export async function upsertProfile(authUserId, updates) {
  if (!authUserId) {
    const error = new Error('authUserId is required');
    await logOnboardingError({
      error,
      action: 'upsert_profile',
      metadata: { reason: 'missing_auth_user_id' },
    });
    return { data: null, error };
  }

  // Use Supabase's native upsert with conflict resolution
  // This handles both insert and update in one operation
  const payload = {
    auth_user_id: authUserId,
    ...updates,
  };

  const { data, error } = await supabase
    .from('profiles')
    .upsert(payload, {
      onConflict: 'auth_user_id',
      ignoreDuplicates: false,
    })
    .select()
    .maybeSingle();

  // Log error if upsert failed
  if (error) {
    await logOnboardingError({
      error,
      action: 'upsert_profile',
      authUserId,
      userEmail: updates.email,
      metadata: { updates: Object.keys(updates) },
    });
  }

  return { data, error };
}

export async function updateProfileFlags(authUserId, flags) {
  return upsertProfile(authUserId, flags);
}

export async function logWelcomeEmailTrigger(email) {
  // TODO: Replace with actual welcome email trigger (Edge Function / external service).
  console.info('Welcome email triggered for', email);
}

export async function sendPasswordResetEmail(email) {
  const { data, error } = await supabase.auth.resetPasswordForEmail(email, {
    redirectTo: `${window.location.origin}/onboarding`,
  });

  // Log error if password reset failed
  if (error) {
    await logOnboardingError({
      error,
      action: 'password_reset',
      userEmail: email,
    });
  }

  return { data, error };
}

// Fitness Test API functions

export async function saveFitnessTestResult(authUserId, testData) {
  if (!authUserId) {
    const error = new Error('authUserId is required');
    await logOnboardingError({
      error,
      action: 'save_fitness_test',
      metadata: { reason: 'missing_auth_user_id' },
    });
    return { data: null, error };
  }

  const payload = {
    auth_user_id: authUserId,
    ...testData,
  };

  const { data, error } = await supabase
    .from('fitness_test_results')
    .upsert(payload, {
      onConflict: 'auth_user_id,test_date',
      ignoreDuplicates: false,
    })
    .select()
    .single();

  if (error) {
    await logOnboardingError({
      error,
      action: 'save_fitness_test',
      authUserId,
      metadata: { testData: Object.keys(testData) },
    });
  }

  return { data, error };
}

export async function getFitnessTestResults(authUserId) {
  const { data, error } = await supabase
    .from('fitness_test_results')
    .select('*')
    .eq('auth_user_id', authUserId)
    .order('test_date', { ascending: false });

  if (error) {
    await logOnboardingError({
      error,
      action: 'get_fitness_tests',
      authUserId,
    });
  }

  return { data, error };
}

export async function getLatestFitnessTest(authUserId) {
  const { data, error } = await supabase
    .from('fitness_test_results')
    .select('*')
    .eq('auth_user_id', authUserId)
    .order('test_date', { ascending: false })
    .limit(1)
    .maybeSingle();

  if (error) {
    await logOnboardingError({
      error,
      action: 'get_latest_fitness_test',
      authUserId,
    });
  }

  return { data, error };
}
